import { useState } from "react";
import { Link } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import { useBreakpoints } from "../../../../hooks/useBreakpoints";

export interface IMenuItemLink {
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
}

interface IMenuItemProps {
  label: string;
  links?: IMenuItemLink[];
  to?: string;
  triggerContent?: React.ReactNode;
  triggerClassName?: string;
  triggerStyle?: React.CSSProperties;
  menuHeader?: React.ReactNode;
}

const DEFAULT_TRIGGER_CLASS = "nav-item-toggle";

function MenuItemDropdown({ label, links, triggerContent, triggerClassName, triggerStyle, menuHeader }: IMenuItemProps) {
  return (
    <Dropdown>
      <Dropdown.Toggle as="div" bsPrefix=" " className={triggerClassName ?? DEFAULT_TRIGGER_CLASS} style={triggerStyle}>
        {triggerContent ?? (
          <>
            {label}
            <i className="bi bi-chevron-down nav-item-toggle-chevron" />
          </>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className="menu menu-sub menu-sub-dropdown menu-column menu-rounded p-5 w-300px">
        {menuHeader && (
          <>
            <div className="menu-item px-3">
              <div className="menu-content px-3">{menuHeader}</div>
            </div>
            <div className="separator my-2" />
          </>
        )}
        {links!.map((link) =>
          link.to ? (
            <Dropdown.Item key={link.label} as={Link} to={link.to} onClick={link.onClick} bsPrefix="nav-item">
              {link.label}
            </Dropdown.Item>
          ) : link.href ? (
            <Dropdown.Item key={link.label} href={link.href} onClick={link.onClick} bsPrefix="nav-item">
              {link.label}
            </Dropdown.Item>
          ) : (
            <Dropdown.Item key={link.label} as="button" onClick={link.onClick} bsPrefix="nav-item">
              {link.label}
            </Dropdown.Item>
          )
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function MenuItemAccordion({ label, links, triggerContent, triggerStyle, menuHeader }: IMenuItemProps) {
  const [open, setOpen] = useState(false);
  const collapseId = `accordion-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div className="accordion-item border-0">
      <button
        className="nav-item-toggle shadow-none"
        type="button"
        style={triggerStyle}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={collapseId}
      >
        {triggerContent ?? label}
        <i className="bi bi-chevron-down nav-item-toggle-chevron" />
      </button>

      <div id={collapseId} className={`accordion-collapse collapse${open ? " show" : ""}`}>
        {menuHeader && (
          <div className="px-2 py-1 text-muted small">{menuHeader}</div>
        )}
        <div className="d-flex flex-column ps-2">
          {links!.map((link) =>
            link.to ? (
              <Link key={link.label} className="nav-item" to={link.to} onClick={link.onClick}>
                {link.label}
              </Link>
            ) : link.href ? (
              <a key={link.label} className="nav-item" href={link.href} onClick={link.onClick}>
                {link.label}
              </a>
            ) : (
              <button key={link.label} className="nav-item" onClick={link.onClick}>
                {link.label}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default function MenuItem({ label, links, to, triggerContent, triggerClassName, triggerStyle, menuHeader }: IMenuItemProps) {
  const { isSm } = useBreakpoints();
  const isDropdown = !to && links && links.length > 0;

  // Direct link — no dropdown
  if (!isDropdown) {
    const className = triggerClassName ?? DEFAULT_TRIGGER_CLASS;
    return to ? (
      <Link to={to} className={className} style={triggerStyle}>
        {triggerContent ?? <span>{label}</span>}
      </Link>
    ) : (
      <div className={className} style={triggerStyle}>
        {triggerContent ?? <span>{label}</span>}
      </div>
    );
  }

  if (isSm) {
    return (
      <MenuItemAccordion
        label={label}
        links={links}
        triggerContent={triggerContent}
        triggerStyle={triggerStyle}
        menuHeader={menuHeader}
      />
    );
  }

  return (
    <MenuItemDropdown
      label={label}
      links={links}
      triggerContent={triggerContent}
      triggerClassName={triggerClassName}
      triggerStyle={triggerStyle}
      menuHeader={menuHeader}
    />
  );
}
